import React2, { createContext, useMemo, useContext } from 'react';

// src/features/featureDefaults.ts
var FEATURE_DEFAULTS = {
  calls: true,
  videophone: true,
  openDoor: true,
  camera: true,
  contacts: true,
  photos: true,
  voicemail: true,
  callHistory: true,
  games: true,
  /**
   * ── OFF BY DEFAULT, DELIBERATELY, AND THE ONLY ONE THAT IS ────────────────
   *
   * Every other surface here is a long-standing capability that buildings tighten
   * downward. The agenda is being ROLLED OUT, and a rollout that starts switched on
   * everywhere is not a rollout.
   *
   * `resolveFeatureChain` gives this most-specific-wins semantics over
   * Site -> Building -> Floor -> Unit -> Room, so a single apartment can be opened
   * while its site stays closed. That is exactly the control the rollout needs, and it
   * is why `agenda` is deliberately NOT added to HARD_GATED_FEATURES: sticky-OFF would
   * make a unit un-openable under a site that has not been opened yet, which is the
   * opposite of what is wanted here.
   *
   * Flipping this to `false` costs nothing today: `resident_events` was dropped and
   * `agenda_entries` is empty, so every tablet in the field currently renders an EMPTY
   * agenda. This removes an empty page until someone deliberately opens it.
   *
   * ⚠ MIRRORED in CARO-Api `controllers/residentAppController.js` — that file is the
   * authoritative enforcement point (the tablet only ever receives the resolved map),
   * and the two must not disagree.
   */
  agenda: false,
  weather: true,
  news: true,
  info: true,
  /**
   * ── OFF BY DEFAULT, AND FOR A DIFFERENT REASON THAN `agenda` ──────────────
   *
   * `agenda` is off because a rollout that starts switched on everywhere is not a
   * rollout. This one is off because of what sits behind it.
   *
   * Measured on the reference site: 769 Qbus outputs, of which 195 carry the `F_`
   * prefix — `F_BRAND_ALARM`, `F_BRAND_DRUKKNOP`, `F_PANIEK_SET`, `F_VOORDEUR_SLOT`.
   * Every one of them is `function_block_type: 'onoff'`, so nothing in the data
   * distinguishes a fire alarm from a bedroom light. `F_VOORDEUR_SLOT` is the exact
   * relay the openDoor automation pulses, which means an unguarded domotics surface
   * routes straight around openDoor's hard gate.
   *
   * So the direction of this default is a safety property, not a rollout convenience:
   * flipping it to `true` would put those controls on every tablet in the fleet the
   * moment the code deploys, before anyone has classified a single output.
   *
   * Unlike `agenda`, this key is ALSO in SITE_OPT_IN_FEATURES — a site must say yes
   * explicitly, and a unit cannot open it on a silent site.
   *
   * ⚠ MIRRORED in CARO-Api `controllers/residentAppController.js`, which is the
   * authoritative enforcement point. `tests/featureResolverContract.test.js` fails if
   * the two drift.
   */
  domotics: false,
  // Always-on / non-toggleable.
  emergency: true
};

// src/features/mergeFeatureFlags.ts
function mergeFeatureFlags(codeDefaults, buildingOverrides) {
  const merged = { ...codeDefaults, ...buildingOverrides ?? {} };
  merged.emergency = true;
  return merged;
}

// src/features/resolveFeatureChain.ts
var HARD_GATED_FEATURES = ["openDoor"];
var SITE_OPT_IN_FEATURES = ["domotics"];
var SITE_LOCATION_TYPE = 1;
function isSiteOptIn(feature) {
  return SITE_OPT_IN_FEATURES.includes(feature);
}
function isHardGated(feature) {
  return HARD_GATED_FEATURES.includes(feature);
}
function overridesOf(entry) {
  const f = entry?.features;
  if (f && typeof f === "object" && !Array.isArray(f)) return f;
  return {};
}
function resolveFeatureChain(codeDefaults, chain) {
  const entries = Array.isArray(chain) ? chain : [];
  let merged = { ...codeDefaults };
  for (const entry of entries) {
    merged = { ...merged, ...overridesOf(entry) };
  }
  for (const key of HARD_GATED_FEATURES) {
    if (entries.some((entry) => overridesOf(entry)[key] === false)) {
      merged[key] = false;
    }
  }
  for (const key of SITE_OPT_IN_FEATURES) {
    const siteOpenedIt = entries.some(
      (entry) => Number(entry?.locationType) === SITE_LOCATION_TYPE && overridesOf(entry)[key] === true
    );
    if (!siteOpenedIt) merged[key] = false;
  }
  merged.emergency = true;
  return merged;
}
function closeHardGates(codeDefaults) {
  const out = { ...codeDefaults };
  for (const key of HARD_GATED_FEATURES) {
    out[key] = false;
  }
  for (const key of SITE_OPT_IN_FEATURES) {
    out[key] = false;
  }
  out.emergency = true;
  return out;
}
function resolveFeatureLock(ancestors, feature) {
  if (!isHardGated(feature)) return { locked: false };
  for (const entry of ancestors ?? []) {
    if (overridesOf(entry)[feature] === false) {
      return {
        locked: true,
        lockedById: entry.locationId ?? null,
        lockedByName: entry.locationName ?? null
      };
    }
  }
  return { locked: false };
}
function resolveFeatureLocks(ancestors) {
  const out = {};
  for (const key of HARD_GATED_FEATURES) {
    out[key] = resolveFeatureLock(ancestors, key);
  }
  return out;
}
var FeaturesContext = createContext(null);
function FeatureProvider({
  features,
  children
}) {
  const value = useMemo(() => {
    const isEnabled = (feature) => {
      if (feature === "emergency") return true;
      return features[feature] === true;
    };
    return { features, isEnabled };
  }, [features]);
  return React2.createElement(FeaturesContext.Provider, { value }, children);
}
function useFeatures() {
  const ctx = useContext(FeaturesContext);
  if (!ctx) {
    throw new Error("useFeatures must be used within a <FeatureProvider>");
  }
  return ctx;
}
function FeatureGate({
  feature,
  children,
  fallback = null
}) {
  const { isEnabled } = useFeatures();
  if (!isEnabled(feature)) {
    return fallback ?? null;
  }
  return React2.createElement(React2.Fragment, null, children);
}

// src/types/features.ts
var TOGGLEABLE_FEATURES = [
  "calls",
  "videophone",
  "openDoor",
  "camera",
  "contacts",
  "photos",
  "voicemail",
  "callHistory",
  "games",
  "agenda",
  "weather",
  "news",
  "info",
  /**
   * Domotics on the tablet (spec 20260826-domotica-via-resident-app).
   *
   * ⚠ Two things about this key are unlike every other one here.
   *
   * 1. It defaults to `false` (featureDefaults.ts). Behind it sit relays that are
   *    indistinguishable from lamps in the data: on the reference site 195 of 769
   *    outputs are `F_` — fire alarm, panic, and the door lock the openDoor gate
   *    exists to protect.
   * 2. It is SITE-OPT-IN (resolveFeatureChain.ts). A site must write an explicit
   *    `true`; silence closes it at every descendant. That is a third merge mode,
   *    distinct from both most-specific-wins and the sticky-OFF hard gate.
   */
  "domotics"
];

export { FEATURE_DEFAULTS, FeatureGate, FeatureProvider, HARD_GATED_FEATURES, SITE_LOCATION_TYPE, SITE_OPT_IN_FEATURES, TOGGLEABLE_FEATURES, closeHardGates, isHardGated, isSiteOptIn, mergeFeatureFlags, resolveFeatureChain, resolveFeatureLock, resolveFeatureLocks, useFeatures };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map